import buttonShapes from "@insite/mobius/Button/buttonShapes";
import hoverAnimations from "@insite/mobius/Button/hoverAnimations";
import { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button/Props";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import get from "@insite/mobius/utilities/get";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import buttonSizeVariants from "@waytek/MobiusOverrides/Button/buttonSizeVariants";
import buttonTypes from "@waytek/MobiusOverrides/Button/buttonTypes";
import { colorPalette } from "@waytek/Utilities/variables";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { applyStyleModifiers } from "styled-components-modifiers";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
        // this is needed because styled-components is confused
        disabled: boolean;
    };

/* Verndale Custom Feature */
const primaryHoverStyles = `
    background-color: ${colorPalette.common.global.white};
    color: ${colorPalette.secondary.main};
    border-color: ${colorPalette.secondary.main};
    &::after {
        width: 60%;
        background-color: ${colorPalette.secondary.main};
        clip-path: polygon(0 0, calc(100% - 4px) 0, 100% 100%, 0 100%);
    }
`;

const secondaryHoverStyles = `
    background-color: ${colorPalette.secondary.main};
    color: ${colorPalette.common.global.white};
    border-color: ${colorPalette.secondary.main};
    &::after {
        left: -1px;
        width: 60%;
        background-color: ${colorPalette.common.global.white};
        clip-path: polygon(0 0, calc(100% - 4px) 0, 100% 100%, 0 100%);
    }
    ${ButtonIcon} {
        color:  ${colorPalette.common.global.white};
    }
`;

const tertiaryHoverStyles = `
    background-color: ${colorPalette.common.global.white};
    color: ${colorPalette.common.global.fieldGreen};
`;

// this should be BWT but typescript is not happy with "Type instantiation is excessively deep and possibly infinite." and nothing else was working
const ButtonWrapper = styled.button<any>`
    cursor: pointer;
    font-family: inherit;
    min-height: ${({ _sizeVariant, _size }) => _size || get(buttonSizeVariants, [_sizeVariant, "height"])}px;
    line-height: 1;
    backface-visibility: hidden;
    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
    word-break: keep-all;
    position: relative;
    box-shadow: ${({ shadow, theme }) => {
        if (shadow) {
            return get(theme, "shadows.1");
        }
        return "none";
    }};
    padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};
    ${applyStyleModifiers(buttonShapes, "_shape")}
    ${applyStyleModifiers(buttonTypes, "buttonType")}
    ${applyStyleModifiers(hoverAnimations, "hoverAnimation")}
    border-width: 1px;
    &:disabled {
        cursor: not-allowed;
    }
    ${ButtonIcon} {
        position: relative;
        top: 0.125em;
    }
    &:focus {
        outline-offset: 1px;
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
    }
    &::-moz-focus-inner {
        border: none;
    }
    &:not(:disabled):hover {
        ${({ variantCustomProp }) => {
            if (variantCustomProp === "primary") {
                return primaryHoverStyles;
            }
            if (variantCustomProp === "secondary") {
                return secondaryHoverStyles;
            }
            if (variantCustomProp === "tertiary") {
                return tertiaryHoverStyles;
            }
        }}
    }
    &::after {
        position: absolute;
        display: inline-block;
        bottom: -1px;
        left: 0;
        content: "";
        width: 0;
        transition: width 0.2s ease-in-out;
        transition-delay: 0.2s;
        height: ${({ _sizeVariant }) => {
            if (_sizeVariant === "large") {
                return "6px";
            }
            if (_sizeVariant === "medium") {
                return "4px";
            }
            if (_sizeVariant === "small") {
                return "4px";
            }
        }};
    }
    ${injectCss}
`;

const omitKeys = ["color", "shape", "size", "sizeVariant"] as const;

type ButtonContextProps = ThemeProps<BaseTheme> & HasDisablerContext;

const Button: React.FC<ButtonProps & ButtonContextProps> = props => {
    const {
        children,
        css: buttonCss,
        disable,
        disabled,
        forwardAs,
        icon,
        typographyProps,
        theme,
        variant,
        mergeCss,
        isFocusAfterMount,
        ...otherProps
    } = props as Omit<ButtonProps, "variant"> & Required<Pick<ButtonProps, "variant">> & ButtonContextProps; // Accounts for defaultProps.
    const position = icon?.position;
    const src = icon?.src;
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);

    let variantProps: ButtonPresentationProps = {};
    if (variant) {
        variantProps = theme.button[variant];
    }
    const { applyProp, applyStyledProp, spreadProps } = applyPropBuilder(props, {
        component: "button",
        propKey: variant,
    });
    const sizeVariant = applyProp("sizeVariant", "medium") as keyof typeof buttonSizeVariants;
    const size = applyProp("size", null) as number | null;
    const resolvedMergeCss = mergeCss ?? variantProps.mergeCss;

    React.useEffect(() => {
        if (isFocusAfterMount) {
            buttonRef?.current?.focus();
        }
    }, []);

    return (
        <ButtonWrapper
            {...omitMultiple(variantProps, omitKeys)}
            ref={buttonRef}
            as={forwardAs}
            css={applyStyledProp("css", resolvedMergeCss)}
            _color={applyProp("color", "primary")}
            _shape={applyProp("shape", "rectangle")}
            _sizeVariant={sizeVariant}
            _size={size}
            // Because disabled doesn't accept undefined
            // eslint-disable-next-line no-unneeded-ternary
            disabled={disable || disabled ? true : false}
            {...omitMultiple(otherProps, omitKeys)}
            variantCustomProp={variant}
        >
            {position === "left" ? (
                <ButtonIcon
                    src={src}
                    size={buttonSizeVariants[sizeVariant].icon}
                    css={css`
                        margin-right: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                    `}
                />
            ) : null}
            {typeof children === "string" ? (
                <Typography
                    size={get(buttonSizeVariants, [sizeVariant, "fontSize"]) || buttonSizeVariants.medium.fontSize}
                    mergeCss={mergeCss}
                    {...spreadProps("typographyProps")}
                >
                    {/* Note: if passing an icon into the button, it will only receive button styles
                    if passed as a `ButtonIcon` */}
                    {children}
                </Typography>
            ) : (
                children
            )}
            {position === "right" ? (
                <ButtonIcon
                    src={src}
                    size={buttonSizeVariants[sizeVariant].icon}
                    css={css`
                        margin-left: ${buttonSizeVariants[sizeVariant].iconPadding}px;
                    `}
                />
            ) : null}
        </ButtonWrapper>
    );
};

Button.defaultProps = {
    variant: "primary",
};

Button.displayName = "Button";
/** @component */
export default withDisabler(withTheme(Button));
export * from "@insite/mobius/Button/Props";
export { ButtonIcon, ButtonWrapper };
